/**
 * WOM Configuration for Vessels Client
 *
 * Client-specific configuration including:
 * - API endpoints
 * - Theme colors and styling
 * - Enabled widgets
 * - Custom behavior
 */

const DEFAULT_PRODUCT_HANDLE = "VASMRD001GO_F"; // Vastramay fallback - API: api.thewordofmouth.in/api/reviews/vastramay/{handle}

/**
 * Get dynamic product title from the page
 * Tries multiple methods to detect the product title
 */
function getProductTitle() {
  // Try different methods to get product title
  const titles = [];

  // Method 1: Try to get from h1 element - try multiple selectors
  const selectors = [
    'h1[itemprop="name"]',
    'h1.product-title',
    '.product__title h1',
    '.product-single__title',
    'h1'
  ];

  for (const selector of selectors) {
    const h1Element = document.querySelector(selector);
    if (h1Element && h1Element.textContent) {
      const h1Title = h1Element.textContent.trim().replace(/\s+/g, ' ').replace(/\n/g, '').replace(/\r/g, '');
      titles.push({ method: `H1 (${selector})`, title: h1Title });
      break;
    }
  }

  // Method 2: Try to get from page <title> tag
  const pageTitle = document.title;
  if (pageTitle) {
    // Remove common suffixes like " | Store" or " – Store" or " - Store"
    let cleanPageTitle = pageTitle;

    // Try different separators
    if (cleanPageTitle.includes(' | ')) {
      cleanPageTitle = cleanPageTitle.split(' | ')[0];
    } else if (cleanPageTitle.includes(' – ')) {
      cleanPageTitle = cleanPageTitle.split(' – ')[0];
    } else if (cleanPageTitle.includes(' - ')) {
      cleanPageTitle = cleanPageTitle.split(' - ')[0];
    }

    cleanPageTitle = cleanPageTitle.trim().replace(/\s+/g, ' ');
    titles.push({ method: 'page title', title: cleanPageTitle });
  }

  // Method 3: Try to get from Shopify product object
  if (typeof window.ShopifyAnalytics !== 'undefined' &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.product &&
      window.ShopifyAnalytics.meta.product.name) {
    const shopifyTitle = window.ShopifyAnalytics.meta.product.name.trim();
    titles.push({ method: 'Shopify Analytics', title: shopifyTitle });
  }

  // Method 4: Try to get from page title meta tag
  const titleMeta = document.querySelector('meta[property="og:title"]');
  if (titleMeta && titleMeta.content) {
    const ogTitle = titleMeta.content.trim();
    titles.push({ method: 'og:title meta', title: ogTitle });
  }

  // Select the longest title (most complete)
  if (titles.length > 0) {
    const longestTitle = titles.reduce((prev, current) =>
      current.title.length > prev.title.length ? current : prev
    );

    return longestTitle.title;
  }

  return null;
}

/**
 * Get product identifier from variant SKU for API use.
 * Uses full variant SKU including size (e.g. VASMK504LDnP865CRnD504LD_36).
 * API format: api/reviews/vastramay/{full_sku}
 */
function getProductIdentifierFromSku() {
  const sku = window.womProductSku || (window.womProductSkus && window.womProductSkus[0]);
  return (sku && typeof sku === 'string') ? sku : null;
}

/**
 * Get the current product identifier for API use.
 * Prefers variant SKU (base, size stripped) over product handle.
 * API: api/reviews/vastramay/{identifier} e.g. VASMRD001GO_F
 */
function getProductHandle() {
  // Method 1: Variant SKU (preferred for Vastramay - maps to API product ID)
  const skuId = getProductIdentifierFromSku();
  if (skuId) {
    return skuId;
  }

  // Method 2: Shopify Analytics object
  if (
    typeof window.ShopifyAnalytics !== "undefined" &&
    window.ShopifyAnalytics.meta &&
    window.ShopifyAnalytics.meta.product &&
    window.ShopifyAnalytics.meta.product.handle
  ) {
    return window.ShopifyAnalytics.meta.product.handle;
  }

  // Method 3: Extract from Shopify product URL structure
  const urlMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
  if (urlMatch && urlMatch[1]) {
    return urlMatch[1];
  }

  // Method 4: Meta tags
  const metaUrl = document.querySelector('meta[property="og:url"]');
  if (metaUrl && metaUrl.content) {
    const metaMatch = metaUrl.content.match(/\/products\/([^\/\?]+)/);
    if (metaMatch && metaMatch[1]) {
      return metaMatch[1];
    }
  }

  return DEFAULT_PRODUCT_HANDLE;
}

// Get product handle (use handle instead of title)
// This ensures consistent formatting across all API calls
let cachedProductHandle = null;

function getFormattedProductId() {
  // Return cached value if available
  if (cachedProductHandle) {
    return cachedProductHandle;
  }
  
  // Get product handle from URL or Shopify Analytics
  let handle = null;
  
  // Method 1: Extract from Shopify product URL structure
  const urlMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
  if (urlMatch && urlMatch[1]) {
    handle = urlMatch[1];
  }
  
  // Method 2: Shopify Analytics object
  if (!handle && typeof window.ShopifyAnalytics !== "undefined" &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.product &&
      window.ShopifyAnalytics.meta.product.handle) {
    handle = window.ShopifyAnalytics.meta.product.handle;
  }
  
  // Method 3: Meta tags
  if (!handle) {
    const metaUrl = document.querySelector('meta[property="og:url"]');
    if (metaUrl && metaUrl.content) {
      const metaMatch = metaUrl.content.match(/\/products\/([^\/\?]+)/);
      if (metaMatch && metaMatch[1]) {
        handle = metaMatch[1];
      }
    }
  }
  
  if (handle) {
    // Remove variant suffixes (like -onyx, -black, -blue, etc.)
    // Split by hyphen and remove common variant words
    const variantSuffixes = [
      'onyx', 'black', 'white', 'blue', 'red', 'green', 'grey', 'gray', 'pink', 'yellow', 'orange', 'purple',
      'small', 'medium', 'large', 'xl', 'xxl', 's', 'm', 'l',
      '500ml', '750ml', '1000ml', '1200ml', '1500ml'
    ];
    
    const parts = handle.split('-');
    const cleanedParts = [];
    
    for (const part of parts) {
      // Keep the part if it's not a variant suffix
      if (!variantSuffixes.includes(part.toLowerCase())) {
        cleanedParts.push(part);
      }
    }
    
    const cleanedHandle = cleanedParts.join('-');
    
    // Cache the cleaned handle
    cachedProductHandle = cleanedHandle;
    
    return cleanedHandle;
  }
  
  cachedProductHandle = DEFAULT_PRODUCT_HANDLE;
  return cachedProductHandle;
}

const vesselsWOMConfig = {
  name: "Vastramay",

  // API Configuration
  apiBaseUrl: "https://api.thewordofmouth.in",
  companyId: "vastramay", // Store slug used by new v2 API - UPDATE with client's company ID

  // API toggles
  disableReviewsApi: false,  // Reviews API enabled - uses variant SKU mapping
  disableInstagramApi: true, // No api/instagram-images/vastramay?product_id= calls

  // Dynamic product title getter
  getProductTitle: getProductTitle,

  // getProductHandle returns product handle for API use
  getProductHandle: getProductHandle,

  // DYNAMIC API URL - Returns null when disabled (no reviews API calls)
  get reviewsApiUrl() {
    if (this.disableReviewsApi) return null;
    const productHandle = this.getProductHandle ? this.getProductHandle() : null;

    if (!productHandle) return null;

    return `${this.apiBaseUrl}/api/reviews/${this.companyId}/${encodeURIComponent(productHandle)}?offset=0&limit=100`;
  },

  // DYNAMIC API URL - Returns null when disabled
  get detailedReviewsApiUrl() {
    if (this.disableReviewsApi) return null;
    const productHandle = this.getProductHandle ? this.getProductHandle() : null;

    if (!productHandle) return null;

    return `${this.apiBaseUrl}/api/reviews/${this.companyId}/${encodeURIComponent(productHandle)}?offset=0&limit=100`;
  },

  // Use local JSON data instead of API (set to false to use API)
  useLocalData: false, // Use live API instead of dummy data
  localDataUrl: "/assets/wom-product-data.json", // Fallback path to local JSON file

  // Debug mode (set to false for production)
  debug: false,

  // Theme Colors - Customize for Vastramay
  colors: {
    primary: "#1a1a1a",
    accent: "#922C39", // Vastramay accent color
    background: "#ffffff",
    text: "#374151",
    textLight: "#6b7280",
    border: "#e5e7eb",
    success: "#10b981",
    starColor: "#922C39", // Vastramay brand color for stars
  },

  // Typography
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

  // Spacing and Layout
  spacing: {
    cardRadius: "12px",
    cardPadding: "20px",
    gap: "16px",
  },

  // Widget Configuration - Enable/Disable widgets
  components: {
    socialProof: true, // Shows rating and review count
    liveUsers: true, // Shows "X people viewing now"
    ratingBreakdown: true, // Shows star rating distribution
    reviews: true, // Shows full review cards
  },

  // Override to use company-wide data instead of product-specific
  useCompanyWideData: false, // Use product-specific API with product handle for individual reviews

  // Widget Settings
  settings: {
    liveUpdates: true, // Auto-refresh live data
    showVerifiedBadge: true, // Show verified purchase badges
    reviewsPerPage: 3, // Number of reviews to show per page
  },

  // Custom Callbacks (Optional)
  onWriteReview: function () {
    // Custom logic when "Write Review" is clicked
    // Add your custom modal logic here
  },

  onLoadMore: function () {
    // Custom logic when "Load More" is clicked
    // Add your custom pagination logic here
  },
};

// Export for use
if (typeof module !== "undefined" && module.exports) {
  module.exports = vesselsWOMConfig;
} else {
  // Expose to window with both names for backward compatibility
  window.vesselsWOMConfig = vesselsWOMConfig; // Primary
  window.clientWOMConfig = vesselsWOMConfig; // Alias for backward compatibility
}
